import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import type { AlertDetail } from '../../types'

/**
 * AF-126 — "Why is this alert here?" panel.
 *
 * Reads as: scenario fired → these rules + signals matched → these reason codes.
 * The reason codes are the analyst's quick-reference vocabulary.
 */
export function WhySection({ alert }: { alert: AlertDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Why this alert</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs uppercase tracking-wide text-muted-fg">Triggering scenario</p>
          <p className="text-sm font-medium">
            <span className="font-mono text-xs text-muted-fg">{alert.scenario_code} · </span>
            {alert.scenario_name}
          </p>
        </div>

        {alert.reason_codes.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs uppercase tracking-wide text-muted-fg">Reason codes</p>
            <div className="flex flex-wrap gap-1.5">
              {alert.reason_codes.map((code) => (
                <Badge key={code} variant="info" className="font-mono">
                  {code}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {alert.rules_fired.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-wide text-muted-fg">Rules fired</p>
            <ul className="flex flex-col gap-2">
              {alert.rules_fired.map((rule) => (
                <li
                  key={rule.rule_code}
                  className="rounded border border-border bg-muted/30 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{rule.rule_name}</p>
                    <span className="font-mono text-xs text-muted-fg">{rule.rule_code}</span>
                  </div>
                  {Object.keys(rule.evidence).length > 0 && (
                    <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                      {Object.entries(rule.evidence).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-1">
                          <dt className="text-muted-fg">{k.replace(/_/g, ' ')}:</dt>
                          <dd className="font-mono">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
